import _extends from "@babel/runtime/helpers/extends";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _createSuper from "@babel/runtime/helpers/createSuper";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _unionBy from "lodash/unionBy";
import _values from "lodash/values";
var _excluded = ["registerMarketplaceEntities"];
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getId, isActive, getProductType } from '@nerdwallet/marketplace/selectors/common';
import { PRODUCT_TYPES } from '@nerdwallet/nw-api-sdk/marketplace';
import getDisplayName from '../../lib/get-display-name';
var MarketplaceContext = /*#__PURE__*/React.createContext({});
export default MarketplaceContext;
var COMPLIANCE_OK_FIELDS = ['name', 'product.name', 'institution.name', 'product.institution.name', 'details.seo_name', 'derived_details.reward_cents_per_unit_string'];

var getEntitiesFromProps = function getEntitiesFromProps(props) {
  var _props$data$value, _props$data;

  return _values((_props$data$value = props === null || props === void 0 ? void 0 : (_props$data = props.data) === null || _props$data === void 0 ? void 0 : _props$data.value) !== null && _props$data$value !== void 0 ? _props$data$value : {});
};

export var isCreditCardProduct = function isCreditCardProduct(entity) {
  return getProductType(entity) === PRODUCT_TYPES.CREDIT_CARDS;
};
export var getInactiveEntitiesFromProps = function getInactiveEntitiesFromProps(props) {
  var entities = getEntitiesFromProps(props);
  var inactiveEntities = [];
  if (!entities.length) return []; // Ignore MarketplaceField components when the field is not a compliance-flagged field.
  // We don't need to show the out-of date messaging if this field isn't a compliance concern.

  if (COMPLIANCE_OK_FIELDS.indexOf(props.field) > -1) return [];
  entities.forEach(function (entity) {
    if (!entity || !isCreditCardProduct(entity) || isActive(entity)) {
      return;
    }

    inactiveEntities.push(entity);
  });
  return inactiveEntities;
}; // Provider consumes values from parent
// and falls back to providing its own if undefined

var MarketplaceContextProvider = /*#__PURE__*/function (_React$Component) {
  _inherits(MarketplaceContextProvider, _React$Component);

  var _super = _createSuper(MarketplaceContextProvider);

  function MarketplaceContextProvider() {
    var _this;

    _classCallCheck(this, MarketplaceContextProvider);

    _this = _super.call(this);

    _defineProperty(_assertThisInitialized(_this), "registerMarketplaceEntities", function (props) {
      var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
          _ref$registerInactive = _ref.registerInactive,
          registerInactive = _ref$registerInactive === void 0 ? true : _ref$registerInactive;

      var entities = getEntitiesFromProps(props);
      var inactiveEntities = registerInactive ? getInactiveEntitiesFromProps(props) : [];

      _this.setState(function (oldState) {
        var newState = {};

        var newEntities = _unionBy(oldState.entities, entities, function (entity) {
          return getId(entity);
        });

        if (newEntities.length !== oldState.entities.length) newState.entities = newEntities;

        var newInactiveEntities = _unionBy(oldState.inactiveEntities, inactiveEntities, function (entity) {
          return getId(entity);
        });

        if (newInactiveEntities.length !== oldState.inactiveEntities.length) newState.inactiveEntities = newInactiveEntities;
        return newState;
      });
    });

    _this.state = {
      entities: [],
      inactiveEntities: []
    };
    return _this;
  }

  _createClass(MarketplaceContextProvider, [{
    key: "render",
    value: function render() {
      var _this2 = this;

      var children = this.props.children;
      var _this$state = this.state,
          entities = _this$state.entities,
          inactiveEntities = _this$state.inactiveEntities;
      return /*#__PURE__*/React.createElement(MarketplaceContext.Consumer, null, function (ctx) {
        return /*#__PURE__*/React.createElement(MarketplaceContext.Provider, {
          value: {
            registerMarketplaceEntities: ctx.registerMarketplaceEntities || _this2.registerMarketplaceEntities,
            entities: ctx.entities || entities,
            inactiveEntities: ctx.inactiveEntities || inactiveEntities
          }
        }, children);
      });
    }
  }]);

  return MarketplaceContextProvider;
}(React.Component); // eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration


MarketplaceContextProvider.defaultProps = {}; // eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration

MarketplaceContextProvider.propTypes = {
  children: PropTypes.node.isRequired
};
export { MarketplaceContextProvider };
/**
 * HOC that takes a Component and options object and registers marketplace entities
 * into a MarketplaceContext after entities are resolved in props.data
 *
 * @export
 * @param {*} Component
 * @param {*} { registerInactive = true} Options
 * @returns wrapped component
 */

export function withMarketplaceContext(Component) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  var WithMarketplaceContextImpl = function WithMarketplaceContextImpl(_ref2) {
    var _props$data4;

    var registerMarketplaceEntities = _ref2.registerMarketplaceEntities,
        props = _objectWithoutProperties(_ref2, _excluded);

    useEffect(function () {
      var _props$data2, _props$data3;

      if (props !== null && props !== void 0 && (_props$data2 = props.data) !== null && _props$data2 !== void 0 && _props$data2.loading) return;
      if (!(props !== null && props !== void 0 && (_props$data3 = props.data) !== null && _props$data3 !== void 0 && _props$data3.value)) return;
      registerMarketplaceEntities(props, options);
    }, // eslint-disable-next-line react-hooks/exhaustive-deps -- directive added automatically by Shepherd migration
    [props === null || props === void 0 ? void 0 : (_props$data4 = props.data) === null || _props$data4 === void 0 ? void 0 : _props$data4.value]);
    return /*#__PURE__*/React.createElement(Component, props);
  };

  WithMarketplaceContextImpl.propTypes = _objectSpread({
    registerMarketplaceEntities: PropTypes.func
  }, Component.propTypes);
  WithMarketplaceContextImpl.defaultProps = Component.defaultProps;

  var WithMarketplaceContext = function WithMarketplaceContext(props) {
    return /*#__PURE__*/React.createElement(MarketplaceContext.Consumer, null, function (_ref3) {
      var registerMarketplaceEntities = _ref3.registerMarketplaceEntities;
      return /*#__PURE__*/React.createElement(WithMarketplaceContextImpl, _extends({}, props, {
        registerMarketplaceEntities: registerMarketplaceEntities
      }));
    });
  };

  hoistNonReactStatics(WithMarketplaceContext, Component);
  WithMarketplaceContext.propTypes = Component.propTypes;
  WithMarketplaceContext.defaultProps = Component.defaultProps;
  WithMarketplaceContext.displayName = "WithMarketplaceContext(".concat(getDisplayName(Component), ")");
  return WithMarketplaceContext;
}